/**
 * Single source of truth for the active webinar/event promo shown on the
 * dashboard. Update this object to switch to the next event — the popup
 * (DashboardLayout) and the condensed widget (DashboardMain / AdminHome)
 * both read from here.
 *
 * To retire the active promo without removing the code, set `enabled` to
 * false or bump `hideAfter` into the past — both hide everywhere.
 *
 * To launch a NEW event, change the `storageKey` so previously-dismissed
 * users see the new popup again. Treat the key as the event identity.
 */

export interface WebinarPromoEvent {
  /** Master kill switch. Overrides everything when false. */
  readonly enabled: boolean;
  /** Bumped per-event; tied to localStorage dismiss flag so new events re-show. */
  readonly storageKey: string;
  /** Event start time (ISO 8601 with timezone). Used for display + relative wording. */
  readonly startsAt: string;
  /** Event end time (ISO 8601 with timezone). */
  readonly endsAt: string;
  /** Stop showing both popup and widget at or after this moment (ISO 8601). */
  readonly hideAfter: string;
  /** "Add to calendar" link (Google Calendar template URL, ICS file, etc.). */
  readonly calendarUrl: string;
  /** Direct "join the meeting" link (e.g. Google Meet URL). Shown in place
   * of the calendar CTA while inside the join window. */
  readonly meetingUrl: string;
  /** Start showing the meeting URL this many minutes BEFORE startsAt. */
  readonly meetingLinkLeadMinutes: number;
  /** Keep showing the meeting URL this many minutes AFTER endsAt. */
  readonly meetingLinkTailMinutes: number;
  /** ms to wait after dashboard mounts before opening the popup. */
  readonly popupDelayMs: number;
  /**
   * Optional preview override. When set to a valid ISO 8601 timestamp, all
   * webinar surfaces (card, dialog, countdown) treat that moment as "now"
   * — handy for QA-ing the LIVE state, the join window, and the countdown
   * formats without having to wait. Leave empty/undefined in production.
   */
  readonly previewNow?: string;
  /** i18n keys — the popup reads these from the locale files. */
  readonly i18n: {
    readonly titleKey: string;
    readonly descriptionKey: string;
    readonly dateLabelKey: string;
    readonly ctaKey: string;
    readonly badgeKey: string;
    readonly widgetTitleKey: string;
    readonly widgetCtaKey: string;
    readonly moreDetailsKey: string;
    readonly dismissKey: string;
    readonly liveBadgeKey: string;
    readonly joinNowKey: string;
  };
}

export const ACTIVE_WEBINAR_PROMO: WebinarPromoEvent = {
  enabled: true,
  // previewNow: "2026-05-24T13:15:00+03:00", // LIVE NOW
  // Event slug — change for the next event so prior dismissers see it again.
  storageKey: "webot:webinarPromo:2026-05-24",
  startsAt: "2026-05-24T13:00:00+03:00",
  endsAt: "2026-05-24T14:00:00+03:00",
  hideAfter: "2026-05-24T14:00:00+03:00",
  calendarUrl:
    "https://calendar.google.com/calendar/event?action=TEMPLATE&tmeid=MWVoZWlkaDdxczgybHQzaTMxdjdvcHZsbzEgY19hNDRlOTU1NWQ5YjQ5YjBlMTBlNTRiOGI1NmZlZWQ0ZDFkMjFhYTI2ZTBmNGQ3ZTljNjdiNzU4OTQ4MTM3NjgzQGc&tmsrc=c_a44e9555d9b49b0e10e54b8b56feed4d1d21aa26e0f4d7e9c67b758948137683%40group.calendar.google.com",
  meetingUrl: "https://meet.google.com/ocd-gyzj-gbh?hs=122&authuser=0",
  meetingLinkLeadMinutes: 30,
  meetingLinkTailMinutes: 60,
  popupDelayMs: 2500,

  // ---------------------------------------------------------------------
  // PREVIEW OVERRIDE — leave empty/commented in production.
  // Uncomment one line to freeze "now" at that moment across all webinar
  // surfaces. Useful for verifying the live state, the join CTA window,
  // and the countdown rules without waiting for the real event time.
  // ---------------------------------------------------------------------
  // previewNow: "2026-05-21T13:00:00+03:00", // countdown — 3 days out
  // previewNow: "2026-05-24T10:30:00+03:00", // countdown — 2h 30m out
  // previewNow: "2026-05-24T12:30:00+03:00", // join window just opened (T-30m)
  // previewNow: "2026-05-24T13:15:00+03:00", // LIVE NOW
  // previewNow: "2026-05-24T14:30:00+03:00", // ended, still in tail window
  // previewNow: "2026-05-24T16:00:00+03:00", // all windows closed

  i18n: {
    titleKey: "dashboardPage.widget.webinarPromo.title",
    descriptionKey: "dashboardPage.widget.webinarPromo.description",
    dateLabelKey: "dashboardPage.widget.webinarPromo.dateLabel",
    ctaKey: "dashboardPage.widget.webinarPromo.cta",
    badgeKey: "dashboardPage.widget.webinarPromo.badge",
    widgetTitleKey: "dashboardPage.widget.webinarPromo.widgetTitle",
    widgetCtaKey: "dashboardPage.widget.webinarPromo.widgetCta",
    moreDetailsKey: "dashboardPage.widget.webinarPromo.moreDetails",
    dismissKey: "dashboardPage.widget.webinarPromo.dismiss",
    liveBadgeKey: "dashboardPage.widget.webinarPromo.liveBadge",
    joinNowKey: "dashboardPage.widget.webinarPromo.joinNow"
  }
};

/**
 * Returns true when the promo should be considered "live" — enabled, not
 * past its hide-after date, and (when checking the user-facing surfaces)
 * not dismissed.
 */
export function isWebinarPromoActive(
  event: WebinarPromoEvent = ACTIVE_WEBINAR_PROMO,
  now: Date = new Date()
): boolean {
  if (!event.enabled) return false;
  return now.getTime() < new Date(event.hideAfter).getTime();
}

/**
 * True while the webinar is currently in progress (startsAt ≤ now < endsAt).
 * Used to flip on the "LIVE NOW" badge / pulsing indicator on the surfaces.
 */
export function isWebinarLive(
  event: WebinarPromoEvent = ACTIVE_WEBINAR_PROMO,
  now: Date = new Date()
): boolean {
  const t = now.getTime();
  return t >= new Date(event.startsAt).getTime() && t < new Date(event.endsAt).getTime();
}

/**
 * True during the join window: `meetingLinkLeadMinutes` before startsAt
 * up until `meetingLinkTailMinutes` after endsAt. While true, the CTA on
 * both the popup and the card swaps from "Add to calendar" to "Join the
 * meeting" pointing at `meetingUrl`.
 */
export function shouldShowJoinLink(
  event: WebinarPromoEvent = ACTIVE_WEBINAR_PROMO,
  now: Date = new Date()
): boolean {
  const t = now.getTime();
  const windowStart = new Date(event.startsAt).getTime() - event.meetingLinkLeadMinutes * 60 * 1000;
  const windowEnd = new Date(event.endsAt).getTime() + event.meetingLinkTailMinutes * 60 * 1000;
  return t >= windowStart && t < windowEnd;
}
